import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { CameraIcon, CheckIcon } from '@heroicons/react/solid'
import _, { get, trim } from 'lodash'
import classNames from 'classnames'
import Layout from 'containers/Layout'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import LoadingIcon from 'components/atoms/LoadingIcon'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import useModal from 'hooks/useModal'
import LocationPicker from 'pages/booking/modals/googleMaps'
import { fetchCarFilters } from 'utils/fetchPublicApi'
import { ALLOWED_LISTING_FILTER_TYPES, buildBannerLinkUrl } from 'utils/bannerUrlBuilder'
import { BOOKING_PAGE_CANVAS, BOOKING_PANEL, BOOKING_SECTION_TITLE, BOOKING_CLUSTER, BOOKING_LABEL } from 'pages/booking/bookingUiTokens'

const formatDateInput = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

const BANNER_TYPES = [
  { id: 'carListing', nameKey: 'masterData.bannerTypes.carListing' },
  { id: 'carDetail', nameKey: 'masterData.bannerTypes.carDetail' },
]

// Step sequence differs by link type: external skips the banner-type step.
const INTERNAL_STEPS = [
  { key: 'linkType', stepKey: 'linkType' },
  { key: 'bannerType', stepKey: 'bannerType' },
  { key: 'details', stepKey: 'bannerDetails' },
  { key: 'target', stepKey: 'bannerFilters' },
]

const EXTERNAL_STEPS = [
  { key: 'linkType', stepKey: 'linkType' },
  { key: 'details', stepKey: 'bannerDetails' },
  { key: 'externalLink', stepKey: 'externalLink' },
]

const EMPTY_CAR_DETAIL_TARGET = {
  companyId: '',
  branchId: '',
  rentalCarId: '',
}

const normalizeList = data => {
  if (_.isArray(data)) return data
  if (_.isArray(data?.company)) return data.company
  if (_.isArray(data?.branches)) return data.branches
  if (_.isArray(data?.branch)) return data.branch
  if (_.isArray(data?.rentalCars)) return data.rentalCars
  return []
}

const getCarLabel = car => {
  const make = get(car, 'car.make.make', get(car, 'car.make', ''))
  const model = get(car, 'car.model.modal', get(car, 'car.model', ''))
  const year = get(car, 'modelYear', '')
  const plate = get(car, 'plateNumber', '')
  return trim(`${make} ${model} ${year} ${plate ? `(${plate})` : ''}`)
}

const filterOptionsToSelectionList = (options = [], isArabic) =>
  options.map(opt => ({
    id: String(opt.value),
    name: isArabic && opt.labelAr ? opt.labelAr : opt.label,
  }))

const NON_NEGATIVE_DECIMAL_INPUT_KEYS = new Set(['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'])

const sanitizeNonNegativeDecimalInput = raw => {
  if (raw === '' || raw == null) return ''
  let cleaned = String(raw).replace(/[^\d.]/g, '')
  const dotIndex = cleaned.indexOf('.')
  if (dotIndex !== -1) {
    cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '')
  }
  return cleaned
}

const nonNegativeDecimalInputToValue = cleaned => {
  if (cleaned === '' || cleaned === '.') return undefined
  const n = Number(cleaned)
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

const handleNonNegativeDecimalKeyDown = e => {
  if (NON_NEGATIVE_DECIMAL_INPUT_KEYS.has(e.key) || e.ctrlKey || e.metaKey) return
  if (/^\d$/.test(e.key)) return
  // eslint-disable-next-line
  if (e.key === '.' && !String(e.currentTarget.value).includes('.')) return
  e.preventDefault()
}

const normalizeListingFilters = filters => {
  if (!filters || !_.isObject(filters)) return {}
  const next = {}
  _.forEach(filters, (val, key) => {
    if (key === 'priceRange_from' || key === 'priceRange_to') {
      const normalized = nonNegativeDecimalInputToValue(sanitizeNonNegativeDecimalInput(val))
      if (normalized !== undefined) {
        next[key] = normalized
      }
      return
    }
    if (_.isArray(val)) {
      next[key] = val.map(String)
    } else if (!_.isNil(val) && val !== '') {
      next[key] = String(val)
    }
  })
  return next
}

const BTN_PREV =
  'inline-flex justify-center py-2.5 px-5 border border-slate-300 shadow-sm text-sm font-medium rounded-xl text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400'

const BTN_NEXT =
  'inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-sm font-medium rounded-xl text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:bg-slate-300 disabled:cursor-not-allowed'

const BannerStepper = ({ steps, activeStep, t }) => (
  <div className="w-full">
    <div className="hidden sm:block">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="relative flex w-full flex-col items-center">
            {index < steps.length - 1 && (
              <div className={classNames('absolute top-5 left-[calc(50%+1.25rem)] h-0.5 w-[calc(100%-2.5rem)]', activeStep > step.id ? 'bg-dark-600' : 'bg-slate-200')} />
            )}
            <div
              className={classNames(
                'z-10 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-colors',
                activeStep > step.id && 'bg-dark-600 text-white shadow-md',
                activeStep === step.id && 'border-2 border-dark-600 bg-white text-dark-600 shadow-sm',
                activeStep < step.id && 'border-2 border-slate-200 bg-white text-slate-400'
              )}
            >
              {activeStep > step.id ? <CheckIcon className="h-5 w-5" /> : step.id}
            </div>
            <span className={classNames('mt-2.5 text-center text-xs font-semibold sm:text-sm', activeStep >= step.id ? 'text-dark-600' : 'text-slate-500')}>
              {t(`masterData.steps.${step.stepKey}`)}
            </span>
          </div>
        ))}
      </div>
    </div>

    <div className="flex gap-2 overflow-x-auto pb-1 sm:hidden">
      {steps.map(step => (
        <div
          key={step.id}
          className={classNames(
            'flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ring-1',
            activeStep === step.id && 'bg-dark-600 text-white ring-dark-600',
            activeStep > step.id && 'bg-dark-600/10 text-dark-600 ring-dark-600/30',
            activeStep < step.id && 'bg-white text-slate-500 ring-slate-200'
          )}
        >
          <span
            className={classNames(
              'flex h-5 w-5 items-center justify-center rounded-full text-[10px]',
              activeStep >= step.id ? 'bg-dark-600 text-white' : 'bg-slate-200 text-slate-500'
            )}
          >
            {activeStep > step.id ? '✓' : step.id}
          </span>
          {t(`masterData.steps.${step.stepKey}`)}
        </div>
      ))}
    </div>
  </div>
)

const AddEditAppBanner = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const isArabic = i18n.language === 'ar'

  const [activeStep, setActiveStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    effectiveStartDate: '',
    effectiveEndDate: '',
    active: true,
    linkType: 'internal',
    bannerType: '',
    domains: ['om'],
  })
  const [externalLinkUrl, setExternalLinkUrl] = useState('')
  const [listingFilters, setListingFilters] = useState({})
  const [carDetailTarget, setCarDetailTarget] = useState(EMPTY_CAR_DETAIL_TARGET)
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedFileAr, setSelectedFileAr] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewUrlAr, setPreviewUrlAr] = useState('')
  const imageInputRef = useRef(null)
  const imageArInputRef = useRef(null)

  const [filterDefinitions, setFilterDefinitions] = useState([])
  const [companies, setCompanies] = useState([])
  const [cities, setCities] = useState([])
  const [branches, setBranches] = useState([])
  const [cars, setCars] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [loadingCars, setLoadingCars] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [bookingCountries, setBookingCountries] = useState([])

  const bannerTypeOptions = useMemo(() => BANNER_TYPES.map(item => ({ id: item.id, name: t(item.nameKey) })), [t])
  const bookingCountryOptions = useMemo(
    () =>
      bookingCountries.map(country => ({
        id: country.code,
        name: isArabic ? country.nameAr || country.name : country.name,
      })),
    [bookingCountries, isArabic]
  )

  const linkUrl = useMemo(() => {
    if (formData.linkType === 'external') return trim(externalLinkUrl)
    if (!formData.bannerType) return ''
    return buildBannerLinkUrl(
      {
        bannerType: formData.bannerType,
        listingFilters,
        carDetailTarget,
      },
      location
    )
  }, [formData.linkType, externalLinkUrl, formData.bannerType, listingFilters, carDetailTarget, location])

  const steps = useMemo(() => (formData.linkType === 'external' ? EXTERNAL_STEPS : INTERNAL_STEPS).map((step, index) => ({ ...step, id: index + 1 })), [formData.linkType])
  const totalSteps = steps.length
  const currentStepKey = steps[activeStep - 1]?.key ?? 'linkType'

  const sidebarFilterGroups = useMemo(
    () => filterDefinitions.filter(f => _.includes(ALLOWED_LISTING_FILTER_TYPES, f.filterType) && f.filterType !== 'priceRange'),
    [filterDefinitions]
  )

  const priceRangeGroup = useMemo(() => _.find(filterDefinitions, ['filterType', 'priceRange']), [filterDefinitions])

  useEffect(() => {
    if (id) {
      fetchBannerData()
    }
  }, [id])

  useEffect(() => {
    fetchBookingCountries()
  }, [])

  useEffect(() => {
    if (formData.bannerType === 'carListing' && filterDefinitions.length === 0) {
      fetchCarFilters().then(data => setFilterDefinitions(data))
    }
  }, [formData.bannerType, filterDefinitions.length])

  useEffect(() => {
    if ((formData.bannerType === 'carDetail' || formData.bannerType === 'carListing') && companies.length === 0) {
      fetchCompanies()
    }
  }, [formData.bannerType, companies.length])

  useEffect(() => {
    if (formData.bannerType === 'carListing' && cities.length === 0) {
      fetchCities()
    }
  }, [formData.bannerType, cities.length])

  useEffect(() => {
    if (!selectedFile) return undefined
    const localPreview = URL.createObjectURL(selectedFile)
    setPreviewUrl(localPreview)
    return () => URL.revokeObjectURL(localPreview)
  }, [selectedFile])

  useEffect(() => {
    if (!selectedFileAr) return undefined
    const localPreview = URL.createObjectURL(selectedFileAr)
    setPreviewUrlAr(localPreview)
    return () => URL.revokeObjectURL(localPreview)
  }, [selectedFileAr])

  useEffect(() => {
    if (carDetailTarget.companyId) {
      fetchBranches(carDetailTarget.companyId)
    } else {
      setBranches([])
    }
  }, [carDetailTarget.companyId])

  useEffect(() => {
    if (carDetailTarget.companyId && carDetailTarget.branchId) {
      fetchCarsForBranch(carDetailTarget.companyId, carDetailTarget.branchId)
    } else {
      setCars([])
    }
  }, [carDetailTarget.companyId, carDetailTarget.branchId])

  const fetchCompanies = async () => {
    try {
      const response = await apiGet('companies', { pageSize: 1000 })
      const list = normalizeList(response?.data).filter(c => c.isActive !== false)
      setCompanies(list.map(c => ({ id: String(c._id), name: isArabic && c.nameAr ? c.nameAr : c.name })))
    } catch {
      setCompanies([])
    }
  }

  const fetchBookingCountries = async () => {
    try {
      const response = await apiGet('booking-countries')
      setBookingCountries(response?.data?.countries || [])
    } catch {
      setBookingCountries([])
    }
  }

  const fetchCities = async () => {
    setLoadingCities(true)
    try {
      const response = await apiGet('cities', { pageSize: 500, pageNumber: 1 })
      const list = response?.data?.cities || []
      setCities(
        list
          .filter(c => c.active !== false)
          .map(c => ({
            id: String(c.city),
            name: isArabic && c.cityAr ? c.cityAr : c.city,
            city: c.city,
            cityAr: c.cityAr,
            lat: c.lat,
            lon: c.lon,
          }))
      )
    } catch {
      setCities([])
    } finally {
      setLoadingCities(false)
    }
  }

  const fetchBranches = async companyId => {
    setLoadingBranches(true)
    try {
      const response = await apiGet(`company/${companyId}/branches`, { pageSize: 1000 })
      const list = normalizeList(response?.data)
      setBranches(list.map(b => ({ id: b._id, name: isArabic && b.nameAr ? b.nameAr : b.name })))
    } catch {
      setBranches([])
    } finally {
      setLoadingBranches(false)
    }
  }

  const fetchCarsForBranch = async (companyId, branchId) => {
    setLoadingCars(true)
    try {
      const response = await apiGet('cars', { companyId, branchId, pageNumber: 1, pageSize: 100 })
      const list = normalizeList(response?.data)
      setCars(list.map(c => ({ ...c, id: String(c._id), name: getCarLabel(c) })))
    } catch {
      setCars([])
    } finally {
      setLoadingCars(false)
    }
  }

  const fetchBannerData = async () => {
    try {
      setLoading(true)
      const response = await apiGet(`app-banner/${id}`)
      if (response.success) {
        const data = response.data
        const linkType = data.linkType === 'external' ? 'external' : 'internal'
        const bannerType = linkType === 'external' ? '' : data.bannerType || ''

        setFormData({
          title: data.title || '',
          effectiveStartDate: formatDateInput(data.effectiveStartDate),
          effectiveEndDate: formatDateInput(data.effectiveEndDate),
          active: data.active !== undefined ? data.active : true,
          linkType,
          bannerType,
          domains: _.isArray(data.domains) ? data.domains : ['om'],
        })
        setExternalLinkUrl(linkType === 'external' ? data.linkUrl || '' : '')
        setListingFilters(bannerType === 'carListing' ? normalizeListingFilters(data.listingFilters) : {})
        setCarDetailTarget(
          bannerType === 'carDetail'
            ? {
                companyId: data.carDetailTarget?.companyId ? String(data.carDetailTarget.companyId) : '',
                branchId: data.carDetailTarget?.branchId ? String(data.carDetailTarget.branchId) : '',
                rentalCarId: data.carDetailTarget?.rentalCarId ? String(data.carDetailTarget.rentalCarId) : '',
              }
            : EMPTY_CAR_DETAIL_TARGET
        )
        setPreviewUrl(data.image || '')
        setPreviewUrlAr(data.imageAr || '')

        if (bannerType === 'carListing') {
          fetchCarFilters().then(filterData => setFilterDefinitions(filterData))
        }
        if (bannerType === 'carDetail') {
          fetchCompanies()
        }
      } else {
        notify.error(t('masterData.messages.failedToFetchBannerData'))
        history.push('/master-data?tab=11')
      }
    } catch {
      notify.error(t('masterData.messages.failedToFetchBannerData'))
      history.push('/master-data?tab=11')
    } finally {
      setLoading(false)
    }
  }

  const handleBannerTypeSelect = typeId => {
    setFormData(prev => ({ ...prev, linkType: 'internal', bannerType: typeId }))
    if (typeId !== 'carListing') {
      setListingFilters({})
    }
    if (typeId !== 'carDetail') {
      setCarDetailTarget(EMPTY_CAR_DETAIL_TARGET)
      setBranches([])
      setCars([])
    }
    setActiveStep(prev => prev + 1)
  }

  const handleLinkTypeSelect = nextLinkType => {
    if (nextLinkType === 'external') {
      setFormData(prev => ({ ...prev, linkType: 'external', bannerType: '' }))
      setListingFilters({})
      setCarDetailTarget(EMPTY_CAR_DETAIL_TARGET)
      setBranches([])
      setCars([])
    } else {
      setFormData(prev => ({ ...prev, linkType: 'internal' }))
    }
    setActiveStep(prev => prev + 1)
  }

  const getMultiSelectIds = filterType => {
    const raw = listingFilters[filterType]
    if (!raw) return []
    return _.isArray(raw) ? raw.map(String) : [String(raw)]
  }

  const handleListingFilterMultiChange = (filterType, selected) => {
    const ids = _.isArray(selected) ? selected.map(id => String(id)) : []
    setListingFilters(prev => {
      const next = { ...prev }
      if (ids.length === 0) {
        delete next[filterType]
      } else {
        next[filterType] = ids
      }
      return next
    })
  }

  const clearLocationFilters = prev => {
    const next = { ...prev }
    delete next.fulfillment
    delete next.governorate
    delete next.location
    delete next.lat
    delete next.lon
    return next
  }

  const handleFulfillmentSelect = fulfillment => {
    const isSameSelection = listingFilters.fulfillment === fulfillment
    setListingFilters(prev => {
      // Required field — switching options replaces location; clicking the same option does not clear it
      if (prev.fulfillment === fulfillment) {
        return prev
      }
      const next = clearLocationFilters(prev)
      next.fulfillment = fulfillment
      return next
    })
    if (!isSameSelection && fulfillment === 'delivery') {
      setTimeout(() => openLocationModal(), 0)
    }
  }

  const handlePickupCityChange = selected => {
    setListingFilters(prev => {
      const next = { ...prev, fulfillment: 'pickup' }
      if (!selected?.id) {
        delete next.governorate
        delete next.location
        delete next.lat
        delete next.lon
        return next
      }
      next.governorate = String(selected.city || selected.id)
      next.location = selected.name || selected.city || String(selected.id)
      if (selected.lat != null && selected.lat !== '') {
        next.lat = String(selected.lat)
      } else {
        delete next.lat
      }
      if (selected.lon != null && selected.lon !== '') {
        next.lon = String(selected.lon)
      } else {
        delete next.lon
      }
      return next
    })
  }

  const handleDeliveryLocationSelect = selectedLocation => {
    setListingFilters(prev => {
      const next = { ...prev, fulfillment: 'delivery' }
      delete next.governorate
      next.location = selectedLocation?.address || ''
      next.lat = String(selectedLocation.lat)
      next.lon = String(selectedLocation.lon)
      return next
    })
    closeLocationModal()
  }

  const [LocationModal, openLocationModal, closeLocationModal] = useModal({
    content: (
      <LocationPicker
        onLocationSelect={handleDeliveryLocationSelect}
        onClose={() => closeLocationModal()}
        initialLat={listingFilters.lat ? parseFloat(listingFilters.lat) : undefined}
        initialLon={listingFilters.lon ? parseFloat(listingFilters.lon) : undefined}
      />
    ),
    title: t('masterData.labels.selectOnMap'),
    dimensions: {
      height: '550px',
      width: '650px',
    },
  })

  const handlePriceRangeChange = (field, rawValue) => {
    const cleaned = sanitizeNonNegativeDecimalInput(rawValue)
    setListingFilters(prev => {
      const next = { ...prev }
      if (cleaned === '') {
        delete next[field]
        // eslint-disable-next-line
      } else if (cleaned === '.' || cleaned.endsWith('.')) {
        next[field] = cleaned
      } else {
        const parsed = nonNegativeDecimalInputToValue(cleaned)
        if (parsed === undefined) {
          delete next[field]
        } else {
          next[field] = parsed
        }
      }
      return next
    })
  }

  const handleNext = () => {
    if (currentStepKey === 'linkType') {
      if (!formData.linkType) {
        notify.error(t('masterData.messages.linkTypeRequired'))
        return
      }
    } else if (currentStepKey === 'bannerType') {
      if (!formData.bannerType) {
        notify.error(t('masterData.messages.bannerTypeRequired'))
        return
      }
    } else if (currentStepKey === 'details') {
      if (!id && !selectedFile && !previewUrl) {
        notify.error(t('masterData.messages.bannerImageRequired'))
        return
      }
      if (!formData.effectiveStartDate) {
        notify.error(t('masterData.messages.bannerStartDateRequired'))
        return
      }
      if (formData.effectiveEndDate && new Date(formData.effectiveEndDate) < new Date(formData.effectiveStartDate)) {
        notify.error(t('masterData.messages.bannerEndDateInvalid'))
        return
      }
    } else if (currentStepKey === 'target' && formData.bannerType === 'carListing') {
      if (!listingFilters.fulfillment) {
        notify.error(t('masterData.messages.bannerFulfillmentRequired'))
        return
      }
      if (listingFilters.fulfillment === 'pickup' && !listingFilters.governorate) {
        notify.error(t('masterData.messages.bannerPickupCityRequired'))
        return
      }
      if (listingFilters.fulfillment === 'delivery' && (!listingFilters.lat || !listingFilters.lon)) {
        notify.error(t('masterData.messages.bannerDeliveryLocationRequired'))
        return
      }
    }

    if (activeStep === totalSteps) {
      handleSubmit()
    } else {
      setActiveStep(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1)
    }
  }

  const handleSubmit = async () => {
    if (loading) return
    if (!id && !selectedFile) {
      notify.error(t('masterData.messages.bannerImageRequired'))
      return
    }
    if (!id && !selectedFileAr) {
      notify.error(t('masterData.messages.bannerImageArRequired'))
      return
    }
    if (!formData.effectiveStartDate) {
      notify.error(t('masterData.messages.bannerStartDateRequired'))
      return
    }
    if (formData.effectiveEndDate && new Date(formData.effectiveEndDate) < new Date(formData.effectiveStartDate)) {
      notify.error(t('masterData.messages.bannerEndDateInvalid'))
      return
    }
    const isExternal = formData.linkType === 'external'
    if (!isExternal && !formData.bannerType) {
      notify.error(t('masterData.messages.bannerTypeRequired'))
      return
    }
    if (!isExternal && formData.bannerType === 'carDetail' && !carDetailTarget.rentalCarId) {
      notify.error(t('masterData.messages.bannerCarRequired'))
      return
    }
    if (!linkUrl) {
      notify.error(t('masterData.messages.bannerLinkRequired'))
      return
    }
    if (isExternal && !/^https?:\/\/.+/i.test(linkUrl)) {
      notify.error(t('masterData.messages.externalLinkInvalid'))
      return
    }

    const payload = new FormData()
    if (selectedFile) payload.append('image', selectedFile)
    if (selectedFileAr) payload.append('imageAr', selectedFileAr)
    payload.append('title', formData.title || '')
    payload.append('effectiveStartDate', formData.effectiveStartDate)
    payload.append('effectiveEndDate', formData.effectiveEndDate || '')
    payload.append('active', String(formData.active))
    payload.append('linkType', formData.linkType)
    payload.append('bannerType', isExternal ? '' : formData.bannerType)
    payload.append('linkUrl', linkUrl)
    payload.append('listingFilters', JSON.stringify(!isExternal && formData.bannerType === 'carListing' ? listingFilters : {}))
    payload.append('carDetailTarget', JSON.stringify(!isExternal && formData.bannerType === 'carDetail' ? carDetailTarget : null))
    payload.append('domains', JSON.stringify(formData.domains))

    try {
      setLoading(true)
      const response = id ? await apiPut(`app-banner/${id}`, payload) : await apiPost('app-banner', payload)

      if (response?.success) {
        notify.success(id ? t('masterData.messages.bannerUpdated') : t('masterData.messages.bannerCreated'))
        history.push('/master-data?tab=11')
      }
    } finally {
      setLoading(false)
    }
  }

  const pageTitle = isEdit ? t('masterData.modals.editImage') : t('masterData.modals.addNewImage')
  const INPUT_FIELD =
    'block w-full rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-dark-500 focus:ring-2 focus:ring-dark-500/20'

  const nextDisabledByStep = {
    linkType: !formData.linkType,
    bannerType: !formData.bannerType,
    details: (!id && !selectedFile && !previewUrl) || (!id && !selectedFileAr && !previewUrlAr) || !formData.effectiveStartDate,
    target:
      (formData.bannerType === 'carDetail' && !carDetailTarget.rentalCarId) ||
      !linkUrl ||
      (formData.bannerType === 'carListing' && !listingFilters.fulfillment) ||
      (formData.bannerType === 'carListing' && listingFilters.fulfillment === 'pickup' && !listingFilters.governorate) ||
      (formData.bannerType === 'carListing' && listingFilters.fulfillment === 'delivery' && (!listingFilters.lat || !listingFilters.lon)),
    externalLink: !linkUrl,
  }

  const currentNextDisabled = Boolean(nextDisabledByStep[currentStepKey])

  return (
    <Layout
      background="#f4f5f9"
      headerRight={
        <button
          type="button"
          className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 sm:px-4 text-xs sm:text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
          onClick={() => history.push('/master-data?tab=11')}
        >
          {t('addEditPartner.buttons.goBack')}
        </button>
      }
      breadcrumbs={[
        { name: t('masterData.title'), pathname: '/master-data?tab=11' },
        {
          name: pageTitle,
          pathname: isEdit ? `/master-data/app-banner/edit/${id}` : '/master-data/app-banner/add',
        },
      ]}
      title={pageTitle}
    >
      <div className="relative flex min-h-[50vh] w-full flex-1 overflow-auto bg-slate-50/40">
        <div className="mx-auto w-full max-w-[1600px]">
          <div className={BOOKING_PAGE_CANVAS}>
            <div className={BOOKING_PANEL}>
              <div className="space-y-6">
                <div className="mb-2 border-b border-slate-200/80 pb-6">
                  <BannerStepper steps={steps} activeStep={activeStep} t={t} />
                </div>

                {/* Step: Link Type */}
                {currentStepKey === 'linkType' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.steps.linkTypeHint')}</div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleLinkTypeSelect('internal')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          formData.linkType === 'internal' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {formData.linkType === 'internal' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.steps.linkTypeInternal')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.steps.linkTypeInternalHint')}</p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleLinkTypeSelect('external')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          formData.linkType === 'external' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {formData.linkType === 'external' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.steps.linkTypeExternal')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.steps.linkTypeExternalHint')}</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Banner Type (internal only) */}
                {currentStepKey === 'bannerType' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.steps.bannerTypeHint')}</div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleBannerTypeSelect('carListing')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          formData.bannerType === 'carListing' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {formData.bannerType === 'carListing' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.steps.carListing')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.steps.carListingHint')}</p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleBannerTypeSelect('carDetail')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          formData.bannerType === 'carDetail' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {formData.bannerType === 'carDetail' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.steps.carDetail')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.steps.carDetailHint')}</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Image, Status, Dates, Title */}
                {currentStepKey === 'details' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.steps.bannerDetailsHint')}</div>

                    <div className={classNames(BOOKING_CLUSTER, 'space-y-5')}>
                      <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                        <div className="flex gap-4 shrink-0">
                          <div>
                            <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>
                              {t('masterData.labels.imageEn')} <span className="text-red-500">*</span>
                            </label>
                            <div className="bg-gray relative h-[200px] w-[150px] border border-dashed border-gray-300 rounded-md">
                              <input ref={imageInputRef} className="hidden" accept=".jpg,.jpeg,.png" type="file" onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
                              {previewUrl ? (
                                <span onClick={() => imageInputRef.current?.click()}>
                                  <img src={previewUrl} alt="" className="cursor-pointer bg-cover h-full w-[150px] rounded" />
                                </span>
                              ) : (
                                <div
                                  onClick={() => imageInputRef.current?.click()}
                                  className="bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full cursor-pointer"
                                >
                                  <CameraIcon className="text-gray-400 h-8 w-8" />
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>
                              {t('masterData.labels.imageAr')} <span className="text-red-500">*</span>
                            </label>
                            <div className="bg-gray relative h-[200px] w-[150px] border border-dashed border-gray-300 rounded-md">
                              <input ref={imageArInputRef} className="hidden" accept=".jpg,.jpeg,.png" type="file" onChange={e => setSelectedFileAr(e.target.files?.[0] || null)} />
                              {previewUrlAr ? (
                                <span onClick={() => imageArInputRef.current?.click()}>
                                  <img src={previewUrlAr} alt="" className="cursor-pointer bg-cover h-full w-[150px] rounded" />
                                </span>
                              ) : (
                                <div
                                  onClick={() => imageArInputRef.current?.click()}
                                  className="bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full cursor-pointer"
                                >
                                  <CameraIcon className="text-gray-400 h-8 w-8" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 space-y-4">
                          <div className="flex items-center justify-between">
                            <label className={BOOKING_LABEL}>{t('masterData.columns.status')}</label>
                            <Switch value={formData.active} onChange={checked => setFormData(prev => ({ ...prev, active: checked }))} />
                          </div>

                          <div>
                            <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.titleOptional')}</label>
                            <input
                              type="text"
                              value={formData.title}
                              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                              className={INPUT_FIELD}
                              placeholder={t('masterData.placeholders.enterBannerTitle')}
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>
                                {t('masterData.labels.effectiveStartDate')} <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="date"
                                value={formData.effectiveStartDate}
                                min={new Date().toISOString().slice(0, 10)}
                                onChange={e => setFormData(prev => ({ ...prev, effectiveStartDate: e.target.value }))}
                                className={INPUT_FIELD}
                              />
                            </div>
                            <div>
                              <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.effectiveEndDate')}</label>
                              <input
                                type="date"
                                value={formData.effectiveEndDate}
                                min={formData.effectiveStartDate || new Date().toISOString().slice(0, 10)}
                                onChange={e => setFormData(prev => ({ ...prev, effectiveEndDate: e.target.value }))}
                                className={INPUT_FIELD}
                              />
                            </div>
                          </div>

                          <div>
                            <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.domains')}</label>
                            <p className="mb-3 text-xs text-slate-500">{t('masterData.labels.domainsHint')}</p>
                            <Selection
                              multiple
                              value={formData.domains || []}
                              onChange={ids => setFormData(prev => ({ ...prev, domains: ids || [] }))}
                              list={bookingCountryOptions}
                              className="w-full"
                              title={t('masterData.placeholders.selectCountries')}
                              showSearch
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step: Listing Filters (internal car listing) */}
                {currentStepKey === 'target' && formData.bannerType === 'carListing' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>
                      {t('masterData.labels.receiveMethod')} <span className="text-red-500">*</span>
                    </div>
                    <p className="text-sm text-slate-500">{t('masterData.messages.receiveMethodOptional')}</p>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleFulfillmentSelect('pickup')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          listingFilters.fulfillment === 'pickup' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {listingFilters.fulfillment === 'pickup' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.labels.pickupMyself')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.labels.pickupMyselfHint')}</p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleFulfillmentSelect('delivery')}
                        className={classNames(
                          'flex cursor-pointer gap-3 rounded-2xl border-2 p-5 transition-all text-left',
                          listingFilters.fulfillment === 'delivery'
                            ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20'
                            : 'border-slate-200/80 bg-white hover:border-slate-300'
                        )}
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-current">
                          {listingFilters.fulfillment === 'delivery' && <div className="h-2.5 w-2.5 rounded-full bg-dark-600" />}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">{t('masterData.labels.deliverToMe')}</div>
                          <p className="mt-0.5 text-xs text-slate-500">{t('masterData.labels.deliverToMeHint')}</p>
                        </div>
                      </button>
                    </div>

                    {listingFilters.fulfillment === 'pickup' && (
                      <div className={classNames(BOOKING_CLUSTER, 'space-y-4')}>
                        <div>
                          <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.city')}</label>
                          <Selection
                            value={listingFilters.governorate || ''}
                            onChange={handlePickupCityChange}
                            list={cities}
                            className="w-full"
                            title={loadingCities ? t('common.loading') : t('masterData.placeholders.selectCity')}
                            showSearch
                          />
                        </div>
                      </div>
                    )}

                    {listingFilters.fulfillment === 'delivery' && (
                      <div className={classNames(BOOKING_CLUSTER, 'space-y-3')}>
                        <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.selectedLocation')}</label>
                        {listingFilters.location || (listingFilters.lat && listingFilters.lon) ? (
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-slate-200/80 bg-white px-4 py-3">
                            <div className="text-sm text-slate-700">
                              <div className="font-medium">{listingFilters.location || '—'}</div>
                              {listingFilters.lat && listingFilters.lon && (
                                <div className="mt-1 text-xs text-slate-500">
                                  {listingFilters.lat}, {listingFilters.lon}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={openLocationModal}
                              className="inline-flex justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              {t('masterData.labels.changeLocation')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={openLocationModal}
                            className="inline-flex w-full justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-6 text-sm font-medium text-slate-700 hover:border-dark-500 hover:text-dark-600"
                          >
                            {t('masterData.labels.selectOnMap')}
                          </button>
                        )}
                      </div>
                    )}

                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.labels.listingFilters')}</div>
                    <p className="text-sm text-slate-500">{t('masterData.messages.listingFiltersOptional')}</p>

                    <div className={classNames(BOOKING_CLUSTER, 'space-y-4')}>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.company')}</label>
                          <Selection
                            multiple
                            value={getMultiSelectIds('companyId')}
                            onChange={selected => handleListingFilterMultiChange('companyId', selected)}
                            list={companies}
                            className="w-full"
                            title={t('masterData.labels.company')}
                          />
                        </div>
                        {sidebarFilterGroups.map(group => {
                          if (group.filterType === 'rate') {
                            return (
                              <div key="listing-price-and-rate" className="contents">
                                {priceRangeGroup && (
                                  <>
                                    <div>
                                      <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.priceFrom')}</label>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={listingFilters.priceRange_from ?? ''}
                                        onKeyDown={handleNonNegativeDecimalKeyDown}
                                        onPaste={e => {
                                          e.preventDefault()
                                          handlePriceRangeChange('priceRange_from', e.clipboardData.getData('text'))
                                        }}
                                        onChange={e => handlePriceRangeChange('priceRange_from', e.target.value)}
                                        className={INPUT_FIELD}
                                      />
                                    </div>
                                    <div>
                                      <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.priceTo')}</label>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={listingFilters.priceRange_to ?? ''}
                                        onKeyDown={handleNonNegativeDecimalKeyDown}
                                        onPaste={e => {
                                          e.preventDefault()
                                          handlePriceRangeChange('priceRange_to', e.clipboardData.getData('text'))
                                        }}
                                        onChange={e => handlePriceRangeChange('priceRange_to', e.target.value)}
                                        className={INPUT_FIELD}
                                      />
                                    </div>
                                  </>
                                )}
                                <div>
                                  <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{group.name}</label>
                                  <Selection
                                    multiple
                                    value={getMultiSelectIds(group.filterType)}
                                    onChange={selected => handleListingFilterMultiChange(group.filterType, selected)}
                                    list={filterOptionsToSelectionList(group.options, isArabic)}
                                    className="w-full"
                                    title={group.name}
                                  />
                                </div>
                              </div>
                            )
                          }

                          return (
                            <div key={group.filterType}>
                              <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{group.name}</label>
                              <Selection
                                multiple
                                value={getMultiSelectIds(group.filterType)}
                                onChange={selected => handleListingFilterMultiChange(group.filterType, selected)}
                                list={filterOptionsToSelectionList(group.options, isArabic)}
                                className="w-full"
                                title={group.name}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div>
                      <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.generatedUrl')}</label>
                      <input type="text" readOnly value={linkUrl} className="block w-full rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-2 text-sm text-slate-600" />
                    </div>
                  </div>
                )}

                {/* Step: Car Detail Target (internal car detail) */}
                {currentStepKey === 'target' && formData.bannerType === 'carDetail' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.labels.carDetailTarget')}</div>

                    <div className={classNames(BOOKING_CLUSTER, 'space-y-4')}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.company')}</label>
                          <Selection
                            value={carDetailTarget.companyId || ''}
                            onChange={value =>
                              setCarDetailTarget({
                                companyId: value?.id ? String(value.id) : '',
                                branchId: '',
                                rentalCarId: '',
                              })
                            }
                            list={companies}
                            className="w-full"
                            title={t('masterData.placeholders.selectCompany')}
                          />
                        </div>
                        <div>
                          <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.branch')}</label>
                          <Selection
                            value={carDetailTarget.branchId || ''}
                            onChange={value =>
                              setCarDetailTarget(prev => ({
                                ...prev,
                                branchId: value?.id ? String(value.id) : '',
                                rentalCarId: '',
                              }))
                            }
                            list={branches}
                            className="w-full"
                            title={t('masterData.placeholders.selectBranch')}
                            disabled={!carDetailTarget.companyId || loadingBranches}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>{t('masterData.labels.generatedUrl')}</label>
                      <input
                        type="text"
                        readOnly
                        value={linkUrl || ''}
                        className="block w-full rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-2 text-sm text-slate-600"
                        placeholder={!carDetailTarget.rentalCarId ? t('masterData.placeholders.selectCar') : ''}
                      />
                    </div>

                    {carDetailTarget.branchId && (
                      <div className="min-h-0">
                        <label className={classNames(BOOKING_LABEL, 'mb-3 block')}>
                          {t('masterData.labels.car')} <span className="text-red-500">*</span>
                        </label>
                        <div id="bannerScrollableCarDiv" className="w-full" style={{ overflow: 'auto', height: 'calc(100vh - 520px)', minHeight: '300px' }}>
                          {loadingCars ? (
                            <div className="flex h-52 w-full items-center justify-center">
                              <LoadingIcon />
                            </div>
                          ) : cars.length === 0 ? (
                            <div className="flex h-52 w-full items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 text-sm text-slate-400">
                              {t('masterData.messages.noDataFound', { itemType: t('masterData.labels.car') })}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                              {cars.map(car => {
                                const isSelected = carDetailTarget.rentalCarId === car.id
                                const imageUrl = car.carImages?.[0]?.url || car.car?.carImages?.[0]?.url || '/placeholder-car.jpg'
                                const companyData = _.isArray(car.company) ? car.company[0] : car.company
                                const companyLogo = companyData?.logoUrl || ''
                                const make = get(car, 'car.make.make', get(car, 'car.make', ''))
                                const model = get(car, 'car.model.modal', get(car, 'car.model', ''))
                                const year = car.modelYear || ''
                                const segment = get(car, 'car.marketSegment', '')
                                const seats = get(car, 'car.numberOfSeats', '')
                                const shifter = get(car, 'car.shifter', '')
                                const kmPerDay = car.unlimitedKm || !car.allowedKMPerDay ? 'Unlimited' : `${car.allowedKMPerDay} km/day`
                                const plate = car.plateNumber || ''

                                return (
                                  <div
                                    key={car.id}
                                    className={classNames(
                                      'relative overflow-hidden rounded-2xl border-2 bg-white p-4 shadow-sm transition-all duration-200',
                                      isSelected ? 'border-dark-600 ring-2 ring-dark-600/20' : 'border-slate-200/80 ring-1 ring-slate-900/[0.04] hover:shadow-md'
                                    )}
                                  >
                                    <div className="relative">
                                      <img src={imageUrl} alt="" className="mb-3 h-40 w-full rounded-xl object-cover" />
                                      {companyLogo && (
                                        <img src={companyLogo} alt="" className="absolute right-2 top-2 h-14 w-14 rounded-lg border border-white object-cover shadow-md" />
                                      )}
                                    </div>
                                    <h3 className="text-base font-extrabold tracking-tight text-slate-900">
                                      {make} {model} {year ? `(${year})` : ''}
                                    </h3>
                                    <div className="mt-2 rounded-xl bg-slate-50/90 p-2.5 ring-1 ring-slate-200/60">
                                      <p className="text-xs text-slate-600">
                                        {segment || ''} | {seats || ''} | {shifter || ''} | {kmPerDay}
                                      </p>
                                      {plate && <p className="mt-1 text-xs text-slate-500">{plate}</p>}
                                    </div>
                                    <p className="mt-3 text-lg font-bold tabular-nums text-slate-900">
                                      {car.dailyRate || 0} {t('common.currency')}/{t('bookingProfile.labels.perDay')}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => setCarDetailTarget(prev => ({ ...prev, rentalCarId: car.id }))}
                                      className={classNames(
                                        'mt-3 w-full inline-flex items-center justify-center py-2.5 px-5 border border-transparent text-sm font-medium rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:bg-slate-300 disabled:cursor-not-allowed',
                                        isSelected ? 'bg-dark-600 hover:bg-dark-700' : 'bg-dark-600 hover:bg-dark-700'
                                      )}
                                    >
                                      {isSelected ? t('bookingProfile.buttons.selected') : t('bookingProfile.buttons.selectCar')}
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step: External Link */}
                {currentStepKey === 'externalLink' && (
                  <div className="space-y-6 pt-2">
                    <div className={BOOKING_SECTION_TITLE}>{t('masterData.labels.externalLink')}</div>

                    <div className={classNames(BOOKING_CLUSTER, 'space-y-4')}>
                      <div>
                        <label className={classNames(BOOKING_LABEL, 'mb-2 block')}>
                          {t('masterData.labels.externalLink')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          inputMode="url"
                          value={externalLinkUrl}
                          onChange={e => setExternalLinkUrl(e.target.value)}
                          className={INPUT_FIELD}
                          placeholder={t('masterData.placeholders.enterExternalLink')}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="sticky bottom-0 z-10 -mx-5 mt-8 border-t border-slate-200/80 bg-white/95 px-5 py-4 backdrop-blur-sm sm:-mx-7 sm:px-7 lg:-mx-10 lg:px-10">
                  <div className="flex justify-between gap-3">
                    {activeStep > 1 ? (
                      <button type="button" onClick={handlePrevious} className={BTN_PREV}>
                        {t('bookingProfile.buttons.previous')}
                      </button>
                    ) : (
                      <span />
                    )}

                    {activeStep === totalSteps ? (
                      <button type="button" onClick={handleNext} disabled={currentNextDisabled || loading} className={BTN_NEXT}>
                        {loading ? (
                          <span className="inline-flex items-center justify-center gap-2">
                            <LoadingIcon />
                            {isEdit ? t('masterData.buttons.updating') : t('masterData.buttons.adding')}
                          </span>
                        ) : isEdit ? (
                          t('masterData.buttons.updateImage')
                        ) : (
                          t('masterData.buttons.createBanner')
                        )}
                      </button>
                    ) : (
                      <button type="button" onClick={handleNext} disabled={currentNextDisabled} className={BTN_NEXT}>
                        {t('bookingProfile.buttons.next')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LocationModal />
    </Layout>
  )
}

export default AddEditAppBanner
